import { t } from "@lingui/macro";
import {
  BrandCreditAccountOrderByColumnEnum,
  BrandCreditAccountsWhereColumn,
  type GetCreditAccountsQuery,
  SortOrder,
  SqlOperator,
} from "@src/__generated__/graphql";
import {
  commonQueryVariables,
  commonSearchParams,
} from "@src/utils/apolloHelpers";
import { setFilterOptions } from "@src/utils/components/filters";
import { Filter, Filters } from "@src/utils/components/filters/models";
import { OrderBy } from "@src/utils/components/sorting/OrderBy";
import { PaginationState } from "@src/utils/mobx/states/PaginationState";
import { filter, map } from "lodash";
import { computed, makeObservable, observable } from "mobx";
import {
  type BrandCreditAccountsSummaryModel,
  CreditAccountModel,
} from "./models";

export enum QuickFilters {
  All = "all",
  Active = "active",
  Archived = "archived",
}

const QuickFilterDefault = QuickFilters.Active;

/**
 * store holding projects page related data
 */
export class StoreCreditAccounts {
  refetch?: () => void;
  @observable asExternalModule: boolean;
  hideColsInExternalModule = {
    all: ["brand"],
  };
  pagination = new PaginationState("creditAccounts");

  orderBy = new OrderBy<BrandCreditAccountOrderByColumnEnum>([
    { column: BrandCreditAccountOrderByColumnEnum.Id, order: SortOrder.Desc },
  ]);

  where = new Filters<BrandCreditAccountsWhereColumn>(
    [
      new Filter({
        column: BrandCreditAccountsWhereColumn.BrandId,
        operator: SqlOperator.In,
        title: t`Client/Brand`,
        tagTitle: t`Brand`,
        options: [],
        sectioned: true,
        hasSelectAllOption: true,
      }),
      new Filter({
        column: BrandCreditAccountsWhereColumn.ExpenseBudgetCategoryId,
        operator: SqlOperator.In,
        title: t`Expenses`,
        isMultiColumn: true,
        multiColumn: {
          label: t`Budget category`,
          type: "list",
        },
        options: [],
      }),
      new Filter({
        column: BrandCreditAccountsWhereColumn.OurWorkBudgetCategoryId,
        operator: SqlOperator.In,
        title: t`Work`,
        isMultiColumn: true,
        multiColumn: {
          label: t`Budget category`,
          type: "list",
        },
        options: [],
      }),
      new Filter({
        column: BrandCreditAccountsWhereColumn.Status,
        operator: SqlOperator.Eq,
        title: t`Status`,
        options: [],
        value: QuickFilterDefault,
        skipWhenValueEquals: QuickFilters.All,
        hidden: true,
      }),
      new Filter({
        column: BrandCreditAccountsWhereColumn.TeamId,
        operator: SqlOperator.In,
        title: t`Team`,
        options: [],
      }),
      new Filter({
        dateRange: true,
        column: BrandCreditAccountsWhereColumn.CreatedAt,
        operator: SqlOperator.Between,
        title: t`Created date`,
        options: [],
      }),
    ],
    {
      onChange: this.pagination.resetPage,
      tableKey: "credit-accounts",
      orderBy: this.orderBy,
    },
  );

  @observable searchTerm = "";

  @observable.ref creditAccounts: CreditAccountModel[] = [];

  @observable.ref brandCreditAccountsSummary?: BrandCreditAccountsSummaryModel;

  constructor(asExternalModule = false) {
    makeObservable(this);
    this.asExternalModule = asExternalModule;
  }

  fromGetCreditAccountsQuery(data: GetCreditAccountsQuery) {
    this.creditAccounts = CreditAccountModel.fromGetCreditAccountsQuery(
      data.brandCreditAccounts,
    );
    if (data.brandCreditAccounts) {
      this.pagination.setFromPaginatorInfo(
        data.brandCreditAccounts.paginatorInfo,
      );
    }
    setFilterOptions(
      this.where,
      BrandCreditAccountsWhereColumn.BrandId,
      filter(
        map(data.clientsSimpleMap, (i) => ({
          value: i?.id,
          label: i?.name,
          options: i?.brands.map((brand) => {
            return {
              label: brand.name,
              value: brand.id,
            };
          })!,
        })),
        (client) => client.options.length,
      ),
    );

    setFilterOptions(
      this.where,
      BrandCreditAccountsWhereColumn.OurWorkBudgetCategoryId,
      map(data.ourWorkBudgetCategories, (i) => ({
        value: i?.id,
        label: i?.name,
      })),
    );

    setFilterOptions(
      this.where,
      BrandCreditAccountsWhereColumn.ExpenseBudgetCategoryId,
      map(data.expenseBudgetCategories, (i) => ({
        value: i?.id,
        label: i?.name,
      })),
    );

    setFilterOptions(
      this.where,
      BrandCreditAccountsWhereColumn.TeamId,
      map(data.teamSimpleMap, (i) => ({
        value: i?.id,
        label: i?.name,
      })),
    );
  }

  @computed get activeQuickFilter() {
    return this.where.filtersByColumn.get(
      BrandCreditAccountsWhereColumn.Status,
    );
  }

  @computed get searchParams() {
    return commonSearchParams(this);
  }

  @computed get queryParams() {
    return commonQueryVariables(this);
  }

  static create() {
    return new StoreCreditAccounts();
  }
}
